import { GoogleGenerativeAI } from "@google/generative-ai";
import CryptoJS from "crypto-js";
import {
	addDoc,
	collection,
	deleteDoc,
	doc,
	getDoc,
	getDocs,
	limit,
	orderBy,
	query,
	updateDoc,
} from "firebase/firestore";
import { Bookmark, ChevronDown, ChevronUp, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import Joyride from "react-joyride";
import { useNavigate, useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { INSTRUCTIONS } from "../constants/instructions";
import { useUser } from "../contexts/UserContext";
import { db } from "../firebaseConfig";

const Journal = ({}) => {
	const { id } = useParams();
	const [currentEntry, setCurrentEntry] = useState("");
	const [journalTitle, setJournalTitle] = useState("");
	const { user, encryptionKey } = useUser(); // Get the current user
	const [showCopilotSuggesions, setShowCopilotSuggesions] = useState(true);
	const [copilotSuggestions, setCopilotSuggestions] = useState([]);
	const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);
	const [modelReady, setModelReady] = useState(false);
	const chatRef = useRef(null);
	const navigate = useNavigate();
	const suggestionsEndRef = useRef(null);
	const [lastProcessedEntry, setLastProcessedEntry] = useState("");
	const [lastProcessedTime, setLastProcessedTime] = useState(Date.now());
	const [runTour, setRunTour] = useState(false);
	const prevSuggestionsLength = useRef(0);
	const [bookmarkedSuggestions, setBookmarkedSuggestions] = useState(
		new Set()
	);

	const JournalEntrySteps = [
		{
			target: ".journal-section",
			content:
				"This is your journal entry. You can edit the title and body of the entry here.",
			placement: "center",
		},
		{
			target: ".journal-copilot-dropdown",
			content:
				"This is your journal copilot. It'll keep generating prompts and suggestions as you write your journal entry.",
			placement: "top",
		},
	];

	useEffect(() => {
		const setDemoState = async () => {
			const userRef = doc(db, "Users", user.uid);
			const userDoc = await getDoc(userRef);
			const userData = userDoc.data();
			if (!userData.journalEntryDemo) {
				setRunTour(true);
				await updateDoc(userRef, { journalEntryDemo: true });
			}
		};

		const fetchJournalEntry = async () => {
			try {
				const entryDoc = doc(db, "Users", user.uid, "entries", id);
				const entrySnapshot = await getDoc(entryDoc);
				if (entrySnapshot.exists()) {
					const entryData = entrySnapshot.data();
					const decryptedTitle = CryptoJS.AES.decrypt(
						entryData.title,
						encryptionKey
					).toString(CryptoJS.enc.Utf8);
					const decryptedBody = CryptoJS.AES.decrypt(
						entryData.body,
						encryptionKey
					).toString(CryptoJS.enc.Utf8);

					setLastProcessedEntry(decryptedBody);
					setLastProcessedTime(Date.now());

					setJournalTitle(decryptedTitle);
					setCurrentEntry(decryptedBody);

					// Load suggestions if they exist
					if (entryData.suggestions) {
						const decryptedSuggestions = entryData.suggestions.map(
							(suggestion) =>
								CryptoJS.AES.decrypt(
									suggestion,
									encryptionKey
								).toString(CryptoJS.enc.Utf8)
						);
						setCopilotSuggestions(decryptedSuggestions);
					}
				} else {
					console.log("No such document!");
				}
			} catch (error) {
				console.error("Error fetching journal entry:", error);
			}
		};
		setDemoState();
		fetchJournalEntry();
	}, [id, user]);

	useEffect(() => {
		const initializeModel = async () => {
			if (!user) return;
			try {
				// Fetch past therapy conversations
				const sessionsRef = collection(
					db,
					"Users",
					user.uid,
					"sessions"
				);
				const q = query(
					sessionsRef,
					orderBy("createdAt", "desc"),
					limit(50)
				);
				const querySnapshot = await getDocs(q);

				let conversationHistory = "";
				querySnapshot.forEach((doc) => {
					const data = doc.data();
					const sessionDate =
						data.createdAt?.toDate()?.toLocaleString() ||
						"Unknown Date";
					conversationHistory += `\n=== Session from ${sessionDate} ===\n`;

					const messageKeys = Object.keys(data)
						.filter((key) => key.startsWith("message_"))
						.sort();

					messageKeys.forEach((key) => {
						const speaker = key.includes("A_User")
							? "User"
							: "Milburn";
						const decryptedMessage = CryptoJS.AES.decrypt(
							data[key],
							encryptionKey
						).toString(CryptoJS.enc.Utf8);
						conversationHistory += `${speaker}: ${decryptedMessage}\n`;
					});
				});

				// Fetch and decrypt past journal entries
				const entriesCollection = collection(
					db,
					"Users",
					user.uid,
					"entries"
				);
				const entriesSnapshot = await getDocs(entriesCollection);
				let journalHistory = "";
				entriesSnapshot.forEach((doc) => {
					const data = doc.data();
					const decryptedTitle = CryptoJS.AES.decrypt(
						data.title,
						encryptionKey
					).toString(CryptoJS.enc.Utf8);
					const decryptedBody = CryptoJS.AES.decrypt(
						data.body,
						encryptionKey
					).toString(CryptoJS.enc.Utf8);
					journalHistory += `Title: ${decryptedTitle}\nEntry: ${decryptedBody}\n\n`;
				});

				const systemInstruction = `${INSTRUCTIONS.JournalCopilot}
				The user ${user.displayName}'s past therapy conversation history is: ${conversationHistory}
				The user's past journal entries are: ${journalHistory}
				Refer to the user's past conversation history and journal entries wherever appropriate, but focus on the current entry. The suggestion should be extremely specific to the current entry. 
				Make your responses concise, considerate, empathetic, and extremely specific: not broad, not overly advisory, and not super critical. 
				Don't use markdown or any other text formatting. Ensure each new suggestion is very different from the previous ones. 
				If there are incomplete sentences at the end, ignore them and don't comment on or refer to them.
				`;

				const model = genAI.getGenerativeModel({
					model: "gemini-1.5-flash",
					systemInstruction,
				});
				chatRef.current = model.startChat();
				setModelReady(true);
			} catch (error) {
				console.error("Error fetching all journal entries:", error);
			}
		};

		initializeModel();
	}, [user]);

	useEffect(() => {
		if (!modelReady) return;

		const fetchAISuggestions = async () => {
			// Only proceed if there's meaningful content and it's different from last processed entry
			if (currentEntry.length + journalTitle.length < 50) return;
			if (currentEntry === lastProcessedEntry) return;

			// Check if enough time has passed or enough new content was added
			const currentTime = Date.now();
			const timeThreshold = 45000; // 45 seconds in milliseconds
			const wordThreshold = 85;

			const timeDiff = currentTime - lastProcessedTime;
			const contentDiff = Math.abs(
				currentEntry.length - lastProcessedEntry.length
			);

			if (timeDiff < timeThreshold && contentDiff < wordThreshold) {
				return;
			}

			try {
				// Update the last processed state
				setLastProcessedEntry(currentEntry);
				setLastProcessedTime(currentTime);

				const result = await chatRef.current.sendMessage(
					`The user's current journal title is """${journalTitle}""" and the journal entry is: """${currentEntry}"""
					 The past suggestions are: """${copilotSuggestions.join("\n")}"""
					 Focus on the final part of the current entry and make the suggestion concise, specific, and different from the previous suggestions. Frame the suggestion as a question. Ignore any incomplete sentences at the end of the current entry.
					`
				);
				const newSuggestion = result.response.text();
				if (newSuggestion !== "") {
					const newSuggestions = [
						...copilotSuggestions,
						newSuggestion,
					];
					setCopilotSuggestions(newSuggestions);
					// Save updated suggestions to database
					await updateSuggestionsInDB(newSuggestions);
				}
			} catch (error) {
				console.error("Error fetching AI suggestions:", error);
			}
		};

		fetchAISuggestions();
	}, [modelReady, currentEntry, journalTitle]);

	useEffect(() => {
		if (suggestionsEndRef.current && copilotSuggestions.length > 0) {
			// Only scroll if we're adding a new suggestion (not removing one)
			const isNewSuggestion =
				copilotSuggestions.length > prevSuggestionsLength.current;
			if (isNewSuggestion) {
				suggestionsEndRef.current.scrollIntoView({
					behavior: "smooth",
				});
			}
		}
		prevSuggestionsLength.current = copilotSuggestions.length;
	}, [copilotSuggestions]);

	const updateJournalEntry = async (field, value) => {
		try {
			const entryDoc = doc(db, "Users", user.uid, "entries", id);
			const encryptedValue = CryptoJS.AES.encrypt(
				value,
				encryptionKey
			).toString();
			await updateDoc(entryDoc, { [field]: encryptedValue });
		} catch (error) {
			console.error("Error updating journal entry:", error);
		}
	};

	const handleTitleChange = (e) => {
		const newTitle = e.target.value;
		setJournalTitle(newTitle);
		updateJournalEntry("title", newTitle);
	};

	const handleEntryChange = (e) => {
		const newEntry = e.target.value;
		setCurrentEntry(newEntry);
		updateJournalEntry("body", newEntry);
	};

	const deleteJournalEntry = async () => {
		try {
			const entryDoc = doc(db, "Users", user.uid, "entries", id);
			await deleteDoc(entryDoc);
			console.log("Journal entry deleted successfully");
			navigate("/journal");
		} catch (error) {
			console.error("Error deleting journal entry:", error);
		}
	};

	const removeSuggestion = async (indexToRemove) => {
		setCopilotSuggestions((prevSuggestions) => {
			const newSuggestions = prevSuggestions.filter(
				(_, index) => index !== indexToRemove
			);
			// Update suggestions in database
			updateSuggestionsInDB(newSuggestions);
			return newSuggestions;
		});
	};

	const updateSuggestionsInDB = async (suggestions) => {
		try {
			const entryDoc = doc(db, "Users", user.uid, "entries", id);
			const encryptedSuggestions = suggestions.map((suggestion) =>
				CryptoJS.AES.encrypt(suggestion, encryptionKey).toString()
			);
			await updateDoc(entryDoc, { suggestions: encryptedSuggestions });
		} catch (error) {
			console.error("Error updating suggestions:", error);
		}
	};

	useEffect(() => {
		const fetchBookmarks = async () => {
			try {
				const bookmarksRef = collection(
					db,
					"Users",
					user.uid,
					"bookmarks"
				);
				const bookmarksSnapshot = await getDocs(bookmarksRef);
				const bookmarkedSet = new Set();
				bookmarksSnapshot.forEach((doc) => {
					bookmarkedSet.add(doc.data().suggestion);
				});
				setBookmarkedSuggestions(bookmarkedSet);
			} catch (error) {
				console.error("Error fetching bookmarks:", error);
			}
		};
		fetchBookmarks();
	}, [user]);

	const toggleBookmark = async (suggestion) => {
		try {
			const bookmarksRef = collection(db, "Users", user.uid, "bookmarks");
			// Add bookmark
			const encryptedSuggestion = CryptoJS.AES.encrypt(
				suggestion,
				encryptionKey
			).toString();
			await addDoc(bookmarksRef, {
				suggestion: encryptedSuggestion,
				createdAt: new Date(),
			});
			setBookmarkedSuggestions((prev) => new Set([...prev, suggestion]));
		} catch (error) {
			console.error("Error adding bookmark:", error);
		}
	};

	return (
		<div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 journal-section">
			<div className="w-full max-w-6xl backdrop-blur-lg rounded-3xl shadow-xl overflow-hidden flex bg-white bg-opacity-90">
				{/* Main Journal Section */}
				<div className="flex-1">
					{/* Title Section */}
					<div className="p-6 border-b border-gray-100">
						<input
							type="text"
							value={journalTitle}
							onChange={handleTitleChange}
							className="text-2xl font-light text-gray-800 text-center w-full bg-transparent focus:outline-none"
							placeholder="What's on your mind today?"
						/>
					</div>

					{/* New Entry Section */}
					<div className="p-6">
						<textarea
							value={currentEntry}
							onChange={handleEntryChange}
							placeholder="Tell me more!"
							className="w-full h-[32rem] p-4 resize-none text-gray-700 focus:outline-none"
						/>
					</div>
				</div>

				{/* Right Sidebar with Dropdowns */}
				<div
					className="w-1/4 border-l border-gray-100 p-2 md:p-4 flex flex-col gap-2 md:gap-4 items-center"
					style={{ height: "75vh" }}>
					{/* Delete Journal Entry Button */}
					<button
						onClick={deleteJournalEntry}
						className="w-full p-1 md:p-2 text-xs md:text-sm bg-red-50 rounded-lg hover:bg-red-100 transition-colors duration-300 text-red-800">
						Delete Entry
					</button>

					{/* Copilot Suggestions Dropdown */}
					<div className="w-full journal-copilot-dropdown">
						<button
							onClick={() =>
								setShowCopilotSuggesions(!showCopilotSuggesions)
							}
							className="w-full flex items-center justify-between p-2 md:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-300">
							<div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2 min-w-0">
								<h2 className="text-xs md:text-sm text-gray-800">
									Copilot
								</h2>
								<span className="text-xs md:text-sm bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded whitespace-nowrap w-fit">
									BETA
								</span>
							</div>
							<div className="flex-shrink-0">
								{showCopilotSuggesions ? (
									<ChevronUp
										size={16}
										className="md:w-5 md:h-5"
									/>
								) : (
									<ChevronDown
										size={16}
										className="md:w-5 md:h-5"
									/>
								)}
							</div>
						</button>

						{showCopilotSuggesions && (
							<div
								className="space-y-2 md:space-y-4 mt-2 md:mt-4 transition-all duration-300 overflow-y-scroll text-xs md:text-sm"
								style={{ maxHeight: "65vh" }}>
								{copilotSuggestions.length === 0 ? (
									<div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-5">
										<p className="text-gray-500 text-center italic">
											AI suggestions will appear here as
											you write...
										</p>
									</div>
								) : (
									copilotSuggestions.map(
										(suggestion, index) => (
											<div
												key={index}
												className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-5 hover:shadow-md transition-all duration-300 relative">
												<div className="pb-1 flex justify-between items-start">
													<button
														onClick={() =>
															toggleBookmark(
																suggestion
															)
														}
														className="hover:bg-gray-100 rounded-full transition-colors"
														disabled={bookmarkedSuggestions.has(
															suggestion
														)}
														data-tooltip-id="bookmark-tooltip"
														data-tooltip-content={
															bookmarkedSuggestions.has(
																suggestion
															)
																? "Bookmark already added"
																: "Bookmark suggestion"
														}>
														<Bookmark
															size={16}
															className={`${
																bookmarkedSuggestions.has(
																	suggestion
																)
																	? "fill-gray-600"
																	: ""
															} text-gray-600`}
														/>
													</button>
													<button
														onClick={() =>
															removeSuggestion(
																index
															)
														}
														className="hover:bg-gray-100 rounded-full transition-colors"
														data-tooltip-id="dismiss-tooltip"
														data-tooltip-content="Dismiss suggestion">
														<X
															size={16}
															className="text-gray-600"
														/>
													</button>
												</div>
												<p className="text-gray-700 leading-relaxed mt-3 md:mt-1">
													{suggestion}
												</p>
											</div>
										)
									)
								)}
								<div ref={suggestionsEndRef} />
							</div>
						)}
					</div>
				</div>
			</div>
			<Joyride
				steps={JournalEntrySteps}
				run={runTour}
				continuous={true}
				showSkipButton={true}
				showProgress={false}
				styles={{
					options: {
						primaryColor: "#60a5fa",
						textColor: "#374151",
					},
					buttonClose: {
						display: "none",
					},
				}}
				locale={{
					last: "Start reflecting!",
				}}
			/>
			<Tooltip
				id="dismiss-tooltip"
				place="bottom"
				className="z-50 text-xs"
				delayShow={200}
			/>
			<Tooltip
				id="bookmark-tooltip"
				place="bottom"
				className="z-50 text-xs"
				delayShow={200}
			/>
		</div>
	);
};

export default Journal;
